import { useState, useEffect, useCallback, useRef } from 'react';
import type { PublicAlertBanner } from '@lnr-v2/types';

const API_BASE_URL =
  import.meta.env.VITE_API_URL || 'http://localhost:4000/api';

// Module-level cache — shared across all component instances
let cachedBanner: PublicAlertBanner | null = null;
let cacheTimestamp: number | null = null;
let fetchPromise: Promise<PublicAlertBanner | null> | null = null;

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const DISMISSED_KEY = 'alert-banner-dismissed';

function isDismissed(bannerId: string): boolean {
  try {
    return sessionStorage.getItem(DISMISSED_KEY) === bannerId;
  } catch {
    return false;
  }
}

function setDismissed(bannerId: string): void {
  try {
    sessionStorage.setItem(DISMISSED_KEY, bannerId);
  } catch {
    // sessionStorage not available
  }
}

async function fetchActiveBanner(): Promise<PublicAlertBanner | null> {
  const now = Date.now();

  // Return cached data if still fresh
  if (cacheTimestamp && now - cacheTimestamp < CACHE_DURATION) {
    return cachedBanner;
  }

  // Deduplicate in-flight requests
  if (fetchPromise) return fetchPromise;

  fetchPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/public/alert-banner`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        mode: 'cors',
        credentials: 'omit',
      });

      if (!response.ok) return null;

      const json = await response.json();
      cachedBanner = json.data || null;
      cacheTimestamp = Date.now();
      return cachedBanner;
    } catch {
      return null;
    } finally {
      fetchPromise = null;
    }
  })();

  return fetchPromise;
}

const PAGE_MAP: Record<string, string> = {
  '/': 'home',
  '/agency': 'agency',
  '/cases': 'cases',
  '/codex': 'codex',
  '/contact': 'contact',
};

function getPageKey(pathname: string): string {
  if (PAGE_MAP[pathname]) return PAGE_MAP[pathname];
  if (pathname.startsWith('/cases/')) return 'case-detail';
  return '';
}

export function useAlertBanner(pathname: string) {
  const [banner, setBanner] = useState<PublicAlertBanner | null>(null);
  const [dismissed, setDismissedState] = useState(false);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    (async () => {
      setLoading(true);
      const data = await fetchActiveBanner();

      if (!mountedRef.current) return;

      if (data) {
        // Check page targeting
        const pageKey = getPageKey(pathname);
        const targeted =
          data.targetPages.length === 0 || data.targetPages.includes(pageKey);

        if (targeted) {
          setBanner(data);
          setDismissedState(isDismissed(data.id));
        } else {
          setBanner(null);
        }
      } else {
        setBanner(null);
      }

      setLoading(false);
    })();

    return () => {
      mountedRef.current = false;
    };
  }, [pathname]);

  const dismiss = useCallback(() => {
    if (banner) {
      setDismissed(banner.id);
      setDismissedState(true);
    }
  }, [banner]);

  return { banner, loading, dismissed, dismiss };
}
